/**
 * Layout principal — 0E3 Workspace V1
 * Rail + Flyout + Header compacto + Workspace ancho completo.
 */
import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { WorkspaceShellProvider, useWorkspaceShell } from '../../contexts/WorkspaceShellContext';
import ModuleOutletGuard from '../common/ModuleOutletGuard';
import LicenseBanner from './LicenseBanner';
import TrialLicenseGate from '../licensing/TrialLicenseGate';
import Footer from './Footer';
import MobileNavigation from './MobileNavigation';
import NavRail from './workspace/NavRail';
import NavMobileDrawer from './workspace/NavMobileDrawer';
import WorkspaceHeader from './workspace/WorkspaceHeader';
import CommandPaletteModal from './workspace/CommandPaletteModal';

const PHONE_BP = 767;
const TABLET_BP = 1024;

function useViewportMode() {
  const [mode, setMode] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    if (w <= PHONE_BP) return 'phone';
    if (w <= TABLET_BP) return 'tablet';
    return 'desktop';
  });

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w <= PHONE_BP) setMode('phone');
      else if (w <= TABLET_BP) setMode('tablet');
      else setMode('desktop');
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return mode;
}

function LayoutInner() {
  const { focusMode, navExpanded } = useWorkspaceShell();
  const viewportMode = useViewportMode();
  const isPhone = viewportMode === 'phone';
  const showRail = !isPhone && !focusMode;

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [viewportMode]);

  const railInteraction = viewportMode === 'desktop' ? 'hover' : 'click';
  const railPadClass = navExpanded ? 'pl-[232px]' : 'pl-16';

  return (
    <div
      className={`e0e3-workspace-root relative flex h-full min-h-0 w-full overflow-hidden bg-e0e3-bg ${focusMode ? 'e0e3-workspace-root--focus' : ''}`}
      data-focus-mode={focusMode ? 'true' : 'false'}
    >
      {showRail && <NavRail interactionMode={railInteraction} />}

      {isPhone && (
        <NavMobileDrawer
          isOpen={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
        />
      )}

      <div
        className={`
          e0e3-workspace-main relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden transition-[padding] duration-200
          ${showRail ? railPadClass : ''}
        `}
      >
        {!focusMode && (
          <WorkspaceHeader
            isPhone={isPhone}
            onOpenMobileMenu={() => setMobileDrawerOpen(true)}
          />
        )}

        {!focusMode && <LicenseBanner compact riskOnly />}

        <main
          className={`
            nexo-admin-content e0e3-app-shell-bg e0e3-workspace-content min-h-0 flex-1 overflow-y-auto overflow-x-hidden
            ${isPhone ? 'p-2 pb-20' : 'p-4 pb-6'}
          `}
        >
          <div className="e0e3-workspace-inner w-full max-w-none">
            <TrialLicenseGate>
              <ModuleOutletGuard>
                <Outlet />
              </ModuleOutletGuard>
            </TrialLicenseGate>
          </div>
        </main>

        {isPhone && !focusMode && <MobileNavigation />}

        {!isPhone && !focusMode && <Footer />}
      </div>

      <CommandPaletteModal />
    </div>
  );
}

const Layout = () => (
  <WorkspaceShellProvider>
    <LayoutInner />
  </WorkspaceShellProvider>
);

export default Layout;
